import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { decodeData } from '../../../utils/encodeAndEncode';
import { getCourseCertificate, createNewCourse, getCourseDetailsById, updateCourseDetails, getActiveCategories,getTraineeGroups, getSubcategoriesByCategory } from '../../../services/adminService';
import AdminPage from '../../../components/admin/AdminPage';
import { toast } from 'react-toastify';

const domain = process.env.REACT_APP_DOMAIN_URL?.replace(/"/g, '').trim();

const currencySymbol = (c) => {
  const map = { USD: '$', EUR: '€', GBP: '£', INR: '₹', SGD: 'S$' };
  return map[c] || c;
};

function CreateCourse() {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  // Only try to decode if courseId exists
  const decodedCourseId = courseId ? decodeData(courseId) : null;
  // // console.log('Course ID:', courseId);
  // // console.log('Decoded Course ID:', decodedCourseId);

  const isEditing = location.state?.isEditing;
  const initialCourseData = location.state?.courseData;

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCourse, setIsLoadingCourse] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [formData, setFormData] = useState({
    courseTitle: '',
    courseDescription: '',
    courseLevel: 'intermediate',
    courseLanguage: 'english',
    courseCategory: '',
    courseSubcategory: '',
    thumbnail: null,
    thumbnailPreview: '',
    certificateType: '',
    courseType: 'free',
    courseDuration: '90',
    currency: domain === 'lms.tpi.sg' ? 'SGD' : 
              (domain === 'lms.nxgenvarsity.com' || domain === 'lms.creatorfoundation.in') ? 'INR' : 'USD',
    price: '',
    tags: [],
    courseInfo: [],
    traineeGroupIds: [],
    isMandatory: false,
  });

  useEffect(() => {
    if (isEditing && initialCourseData) {
      setFormData(initialCourseData);
    }
  }, [isEditing, initialCourseData]);

  useEffect(() => {
    getActiveCategories()
      .then((res) => {
        if (res?.success && Array.isArray(res?.data)) {
          setCategoryOptions(res.data.map(c => ({
            value: c.id,
            label: c.category_name
          })));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (formData.courseCategory) {
      getSubcategoriesByCategory(formData.courseCategory)
        .then((res) => {
          if (res?.success && res?.data?.subcategories) {
            setSubcategoryOptions(res.data.subcategories.map(s => ({
              value: s.id,
              label: s.subcategory_name
            })));
          } else {
            setSubcategoryOptions([]);
          }
        })
        .catch(() => setSubcategoryOptions([]));
    } else {
      setSubcategoryOptions([]);
      setFormData(prev => ({ ...prev, courseSubcategory: '' }));
    }
  }, [formData.courseCategory]);

  // Fetch course details if courseId is available
  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (courseId && decodedCourseId) {
        setIsLoadingCourse(true);
        try {
          const response = await getCourseDetailsById({ course_id: decodedCourseId });

          if (response.success && response.data.course) {
            const course = response.data.course;

            // Map API response to form data structure
            setFormData({
              courseTitle: course.course_name || '',
              courseDescription: course.course_desc || '',
              courseLevel: course.levels || '',
              courseLanguage: course.course_language || '',
              courseCategory: course.course_category || '',
              courseSubcategory: course.course_subcategory || '',
              thumbnail: null, // Will be handled separately if needed
              thumbnailPreview: course.banner_image || '',
              certificateType: course.certificate_template_id?.toString() || '',
              courseType: course.course_type || 'free',
              currency: course.currency || 'USD',
              price: course.course_price || '',
              courseDuration: course.validity?.toString() || '90',
              tags: Array.isArray(course.tags) ? course.tags : [],
              courseInfo: Array.isArray(course.course_info) ? course.course_info : [],
              traineeGroupIds: Array.isArray(course.trainee_group_ids) ? course.trainee_group_ids : [],
              isMandatory: course.is_mandatory === 1,
            });

          } else {
            toast.error('Failed to load course details: ' + (response.data?.error_msg || 'Unknown error'));
          }
        } catch (error) {
          toast.error('Error loading course details. Please try again.');
        } finally {
          setIsLoadingCourse(false);
        }
      }
    };

    fetchCourseDetails();
  }, [courseId, decodedCourseId]);

  const [certificateData, setCertificateData] = useState([]);
  const [traineeGroups, setTraineeGroups] = useState([]);
  const [traineeGroupSearch, setTraineeGroupSearch] = useState('');

  const fetchCertificateData = async () => {
    try {
      const response = await getCourseCertificate();
      if (response.success) {
        setCertificateData(response.data.templates);
      } else {
        // console.error('Invalid certificate data format:', response);
        setCertificateData([]); // Reset to empty array if invalid data
      }
    } catch (error) {
      console.error('Error fetching certificate data:', error);
      setCertificateData([]); // Reset to empty array on error
    }
  };

  useEffect(() => {
    fetchCertificateData();
  }, []);

  const fetchTraineeGroups = async () => {
    try {
      const res = await getTraineeGroups();
      if (res?.success && Array.isArray(res?.data?.groups)) setTraineeGroups(res.data.groups);
      else if (res?.success && Array.isArray(res?.data)) setTraineeGroups(res.data);
    } catch {
      setTraineeGroups([]);
    }
  };
  useEffect(() => {
    fetchTraineeGroups();
  }, []);

  const [tagInput, setTagInput] = useState('');
  const [infoInput, setInfoInput] = useState('');
  const [editingInfo, setEditingInfo] = useState(null);

  // Memoized handlers for tag and info inputs
  const handleTagInputChange = useCallback((e) => {
    setTagInput(e.target.value);
  }, []);

  const handleInfoInputChange = useCallback((e) => {
    setInfoInput(e.target.value);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  }, []);

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prevState => ({
        ...prevState,
        thumbnail: file,
        thumbnailPreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleRemoveFile = (type) => {
    if (type === 'thumbnail') {
      setFormData(prevState => ({
        ...prevState,
        thumbnail: null,
        thumbnailPreview: ''
      }));
    }
  };

  const handleAddTag = useCallback((e) => {
    e.preventDefault();
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      if (tag.length < 3) {
        toast.warning('Tag must be at least 3 characters long');
        return;
      }
      if (formData.tags.length >= 8) {
        toast.warning('Maximum 8 tags allowed');
        return;
      }
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  }, [tagInput, formData.tags]);

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(e);
    }
  };

  const handleAddInfo = useCallback((e) => {
    e.preventDefault();
    if (infoInput.trim()) {
      if (editingInfo !== null) {
        setFormData(prev => ({
          ...prev,
          courseInfo: prev.courseInfo.map((info, index) =>
            index === editingInfo ? infoInput.trim() : info
          )
        }));
        setEditingInfo(null);
      } else {
        setFormData(prev => ({
          ...prev,
          courseInfo: [...prev.courseInfo, infoInput.trim()]
        }));
      }
      setInfoInput('');
    }
  }, [infoInput, editingInfo]);

  const handleEditInfo = (index) => {
    setInfoInput(formData.courseInfo[index]);
    setEditingInfo(index);
  };

  const handleDeleteInfo = (index) => {
    setFormData(prev => ({
      ...prev,
      courseInfo: prev.courseInfo.filter((_, i) => i !== index)
    }));
  };

  const handleInfoInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddInfo(e);
    }
  };

  const steps = [
    { number: '01', title: 'Course Information' },
    { number: '02', title: 'Course Media' },
    { number: '03', title: 'Additional information' },
    { number: '04', title: 'Pricing' },
    { number: '05', title: 'Preview & Submit' }
  ];

  // Validation functions for each step
  const validateStep1 = () => {
    return formData.courseTitle.trim() !== '' &&
           formData.courseDescription.trim() !== '' &&
           formData.courseLevel !== '' &&
           formData.courseLanguage !== '' &&
           formData.courseCategory !== '';
  };

  const validateStep2 = () => {
    // For editing mode, allow progression if thumbnail exists (either new upload or existing image)
    if (courseId && decodedCourseId) {
      return (formData.thumbnail !== null || formData.thumbnailPreview !== '');
    }
    // For new course creation, require new thumbnail upload
    return formData.thumbnail !== null;
  };

  const validateStep3 = () => {
    // For editing mode, allow empty tags and courseInfo if course already exists
    if (courseId && decodedCourseId) {
      return true; // Allow progression in edit mode even with empty arrays
    }
    // For new course creation, require at least one tag and one course info
    return formData.tags.length > 0 && formData.courseInfo.length > 0;
  };

  const validateStep4 = () => {
    if (formData.courseType === 'free') {
      return true;
    }
    return formData.price !== '' && parseFloat(formData.price) > 0;
  };

  const validateStep5 = () => {
    // Preview step - all previous steps must be valid
    return validateStep1() && validateStep2() && validateStep3() && validateStep4();
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return validateStep1();
      case 2:
        return validateStep2();
      case 3:
        return validateStep3();
      case 4:
        return validateStep4();
      case 5:
        return validateStep5();
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (isCurrentStepValid()) {
      setIsSubmitting(true);

      try {
        // Start the minimum 3-second timer
     
        const minLoadingTime = new Promise(resolve => setTimeout(resolve, 3000));

        // Create FormData object for file upload
        const submitData = new FormData();
        const liveDomain = window.location.origin;

        // Add all form fields to FormData
        submitData.append('courseTitle', formData.courseTitle);
        submitData.append('courseDescription', formData.courseDescription);
        submitData.append('courseLevel', formData.courseLevel);
        submitData.append('courseLanguage', formData.courseLanguage);
        submitData.append('courseCategory', formData.courseCategory);
        submitData.append('courseSubcategory', formData.courseSubcategory || '');
        submitData.append('certificateType', formData.certificateType);
        submitData.append('courseType', formData.courseType);
        submitData.append('currency', formData.currency);
        submitData.append('price', formData.price);
        submitData.append('tags', JSON.stringify(formData.tags));
        submitData.append('courseInfo', JSON.stringify(formData.courseInfo));
        submitData.append('liveDomain', liveDomain);
        submitData.append('courseDuration', formData.courseDuration);
        submitData.append('trainee_group_ids', JSON.stringify(formData.traineeGroupIds || []));
        submitData.append('is_mandatory', formData.isMandatory ? '1' : '0');

        // Add thumbnail file if exists (for new upload)
        if (formData.thumbnail) {
          submitData.append('banner_image', formData.thumbnail);
        }

        // Choose API call based on edit mode
        let apiCall;
        if (courseId && decodedCourseId) {
          // Update existing course
          apiCall = updateCourseDetails(decodedCourseId, submitData);
        } else {
          // Create new course
          apiCall = createNewCourse(submitData);
        }

        // Wait for both minimum time and API response
        const [response] = await Promise.all([apiCall, minLoadingTime]);

        if (response.success) {
          toast.success(courseId && decodedCourseId ? 'Course updated successfully' : 'Course created successfully');
          navigate('/admin/courses');
        } else {
          setIsSubmitting(false);
          const errorMsg = courseId && decodedCourseId ? 'Failed to update course' : 'Failed to create course';
          toast.error(errorMsg + ': ' + (response.data?.error_msg || 'Unknown error'));
        }
      } catch (error) {
        setIsSubmitting(false);
        const errorMsg = courseId && decodedCourseId ? 'Error updating course' : 'Error creating course';
        toast.error(errorMsg + '. Please try again.');
      }
    } else {
      toast.warning('Please fill in all required fields before submitting.');
    }
  };

  const inputClass = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF] outline-none transition-all text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

  const renderCourseInformationForm = () => (
    <div className="space-y-6">
      <div>
        <label htmlFor="courseTitle" className={labelClass}>Course Title *</label>
        <input
          type="text"
          className={inputClass}
          id="courseTitle"
          name="courseTitle"
          placeholder="e.g. Introduction to Fire Safety"
          value={formData.courseTitle}
          onChange={handleInputChange}
          maxLength={100}
          required
        />
        <p className="text-gray-500 text-xs mt-1">{formData.courseTitle.length}/100 characters</p>
      </div>

      <div>
        <label htmlFor="courseDescription" className={labelClass}>Course Description *</label>
        <textarea
          className={`${inputClass} min-h-[120px] resize-y`}
          id="courseDescription"
          name="courseDescription"
          rows={4}
          placeholder="Describe what trainees will learn and who this course is for."
          value={formData.courseDescription}
          onChange={handleInputChange}
          maxLength={1000}
          required
        />
        <p className="text-gray-500 text-xs mt-1">{formData.courseDescription.length}/1000 characters</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="courseLevel" className={labelClass}>Level *</label>
          <select className={inputClass} id="courseLevel" name="courseLevel" value={formData.courseLevel} onChange={handleInputChange} required>
            <option value="">Select level</option>
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
            <option value="All Levels">All Levels</option>
          </select>
        </div>
        <div>
          <label htmlFor="courseLanguage" className={labelClass}>Language *</label>
          <select className={inputClass} id="courseLanguage" name="courseLanguage" value={formData.courseLanguage} onChange={handleInputChange} required>
            <option value="">Select language</option>
            <option value="English">English</option>
            <option value="Hindi">Hindi</option>
          </select>
        </div>
        <div>
          <label htmlFor="courseCategory" className={labelClass}>Category *</label>
          <select className={inputClass} id="courseCategory" name="courseCategory" value={formData.courseCategory} onChange={handleInputChange} required>
            <option value="">Select category</option>
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="courseDuration" className={labelClass}>Course Expired *</label>
        <select className={inputClass} id="courseDuration" name="courseDuration" value={formData.courseDuration} onChange={handleInputChange} required>
          <option value="30">1 Month (30 days)</option>
          <option value="60">2 Months (60 days)</option>
          <option value="90">3 Months (90 days)</option>
          <option value="180">6 Months (180 days)</option>
          <option value="365">1 Year (365 days)</option>
          <option value="730">2 Years (730 days)</option>
          <option value="1095">3 Years (1095 days)</option>
        </select>
        <p className="text-gray-500 text-xs mt-1">Duration for which trainees can access this course</p>
      </div>

      {subcategoryOptions.length > 0 && (
        <div>
          <label htmlFor="courseSubcategory" className={labelClass}>Subcategory (Optional)</label>
          <select className={inputClass} id="courseSubcategory" name="courseSubcategory" value={formData.courseSubcategory} onChange={handleInputChange}>
            <option value="">Select subcategory</option>
            {subcategoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className="text-gray-500 text-xs mt-1">
            <Link to="/admin/courses/categories" className="text-[#4F6BFF] hover:underline">Manage categories</Link>
          </p>
        </div>
      )}

      {/* Trainee groups: restrict visibility to selected groups only; leave empty for all */}
      {traineeGroups.length > 0 && (
        <div className="mt-4">
          <label className={labelClass}>Visible to trainee groups (optional)</label>
          <p className="text-gray-500 text-xs mb-2">Leave empty to show to everyone. Select groups to restrict this course to those groups only.</p>
          {traineeGroups.length > 5 && (
            <div className="relative mb-2">
              <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={traineeGroupSearch}
                onChange={(e) => setTraineeGroupSearch(e.target.value)}
                placeholder="Search groups..."
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              {traineeGroupSearch && (
                <button
                  type="button"
                  onClick={() => setTraineeGroupSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <Icon icon="fluent:dismiss-24-regular" width={18} height={18} />
                </button>
              )}
            </div>
          )}
          <div className="max-h-[280px] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-gray-50/30 p-3">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {traineeGroups
                .filter((g) => !traineeGroupSearch.trim() || (g.name || '').toLowerCase().includes(traineeGroupSearch.trim().toLowerCase()))
                .map((g) => (
                  <label key={g.id} className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={(formData.traineeGroupIds || []).includes(g.id)}
                      onChange={() => {
                        const ids = formData.traineeGroupIds || [];
                        const next = ids.includes(g.id) ? ids.filter((id) => id !== g.id) : [...ids, g.id];
                        setFormData((prev) => ({ ...prev, traineeGroupIds: next }));
                      }}
                      className="rounded border-gray-300 text-primary focus:ring-primary/20"
                    />
                    <span className="text-sm text-gray-700">{g.name}</span>
                  </label>
                ))}
            </div>
            {traineeGroupSearch.trim() && traineeGroups.filter((g) => (g.name || '').toLowerCase().includes(traineeGroupSearch.trim().toLowerCase())).length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center">No groups match &quot;{traineeGroupSearch.trim()}&quot;</p>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1.5">{traineeGroups.length} group{traineeGroups.length !== 1 ? 's' : ''} available</p>
        </div>
      )}

      {/* Mandatory Course Section */}
      <div className="mt-6 p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isMandatory}
            onChange={(e) => setFormData(prev => ({ ...prev, isMandatory: e.target.checked }))}
            className="w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
          />
          <div>
            <span className="text-sm font-semibold text-amber-900">⚠️ Mark as Mandatory Course</span>
            <p className="text-xs text-amber-700 mt-0.5">
              {formData.traineeGroupIds?.length > 0
                ? `Users in ${formData.traineeGroupIds.length} selected group(s) will be required to complete this course`
                : 'All users will be required to complete this course'}
            </p>
          </div>
        </label>
      </div>
    </div>
  );

  const renderCourseMediaForm = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Thumbnail */}
      <div>
        <label className={labelClass}>Course Thumbnail *</label>
        <div className="rounded-2xl border border-gray-200 bg-gray-50/30 overflow-hidden">
          {!formData.thumbnailPreview ? (
            <label htmlFor="thumbnail" className="flex flex-col items-center justify-center py-12 px-6 cursor-pointer hover:bg-gray-50/50 transition-colors">
              <input type="file" id="thumbnail" className="hidden" accept="image/*" onChange={handleThumbnailChange} />
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-3">
                <Icon icon="fluent:image-add-24-regular" width="32" height="32" className="text-[#4F6BFF]" />
              </div>
              <span className="text-sm font-medium text-gray-700">Upload thumbnail</span>
              <span className="text-xs text-gray-500 mt-1">Recommended 1280×720px</span>
            </label>
          ) : (
            <div className="relative">
              <img src={formData.thumbnailPreview} alt="Course Thumbnail" className="w-full h-auto object-cover" />
              <button
                type="button"
                onClick={() => handleRemoveFile('thumbnail')}
                className="absolute top-3 right-3 w-9 h-9 rounded-xl bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors"
              >
                <Icon icon="fluent:delete-24-regular" width="18" height="18" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Certificate */}
      <div>
        <label className={labelClass}>Certificate Type (Optional)</label>
        <select
          className={inputClass}
          name="certificateType"
          value={formData.certificateType}
          onChange={handleInputChange}
        >
          <option value="">No Certificate</option>
          {Array.isArray(certificateData) && certificateData.map((cert) => (
            <option key={cert.id || cert._id} value={cert.id || cert._id}>
              {cert.template_name || 'Untitled Certificate'}
            </option>
          ))}
        </select>
        {formData.certificateType && (
          <div className="mt-3 p-4 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
              <Icon icon="fluent:certificate-24-regular" width="24" height="24" className="text-[#4F6BFF]" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Certificate selected</p>
              <p className="text-xs text-gray-500">Issued upon course completion</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAdditionalInformationForm = () => (
    <div className="space-y-8">
      <div>
        <label className={labelClass}>Course Tags *</label>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <input
            type="text"
            className={inputClass}
            placeholder="e.g. Fire Safety, Compliance"
            value={tagInput}
            onChange={handleTagInputChange}
            onKeyDown={handleTagInputKeyDown}
          />
          <button
            type="button"
            className="shrink-0 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm transition-colors"
            onClick={handleAddTag}
          >
            Add Tag
          </button>
        </div>
        <p className="text-gray-500 text-xs mt-1.5">Min 3 characters · Max 8 tags · {8 - formData.tags.length} remaining</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {formData.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-xl bg-gray-100 border border-gray-200 text-sm text-gray-800"
            >
              {tag}
              <button type="button" className="p-0.5 rounded hover:bg-gray-200 text-red-600" onClick={() => handleRemoveTag(tag)} aria-label="Remove tag">
                <Icon icon="fluent:dismiss-24-regular" width="16" height="16" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div>
        <label className={labelClass}>Course Info *</label>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <input
            type="text"
            className={inputClass}
            placeholder="e.g. Lifetime access to materials"
            value={infoInput}
            onChange={handleInfoInputChange}
            onKeyDown={handleInfoInputKeyDown}
          />
          <button
            type="button"
            className="shrink-0 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm transition-colors"
            onClick={handleAddInfo}
          >
            {editingInfo !== null ? 'Update' : 'Add Info'}
          </button>
        </div>
        <div className="mt-3 space-y-2">
          {formData.courseInfo.map((info, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50/50"
            >
              <Icon icon="fluent:checkmark-circle-24-regular" width="20" height="20" className="text-emerald-500 shrink-0" />
              <span className="flex-1 text-sm text-gray-800">{info}</span>
              <div className="flex gap-1">
                <button type="button" className="p-2 rounded-lg hover:bg-gray-200 text-gray-600" onClick={() => handleEditInfo(index)} title="Edit">
                  <Icon icon="fluent:edit-24-regular" width="16" height="16" />
                </button>
                <button type="button" className="p-2 rounded-lg hover:bg-red-50 text-red-600" onClick={() => handleDeleteInfo(index)} title="Remove">
                  <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    const selectedCert = formData.certificateType && certificateData.find(c => (c.id || c._id)?.toString() === formData.certificateType?.toString());
    const selectedCategory = categoryOptions.find(c => c.value?.toString() === formData.courseCategory?.toString());
    const selectedSubcategory = subcategoryOptions.find(s => s.value?.toString() === formData.courseSubcategory?.toString());
    return (
      <div className="space-y-8">
        <p className="text-gray-500 text-sm">Review your course below. Use Previous to edit any step.</p>

        <section>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Icon icon="fluent:info-24-regular" width="18" height="18" className="text-[#4F6BFF]" />
            Course Information
          </h3>
          <div className="rounded-2xl border border-gray-200 bg-gray-50/30 p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Title</p>
                <p className="text-gray-900 font-medium">{formData.courseTitle || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</p>
                <p className="text-gray-900">{selectedCategory?.label || '—'}{selectedSubcategory ? ` / ${selectedSubcategory.label}` : ''}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</p>
              <p className="text-gray-700 text-sm mt-1">{formData.courseDescription || '—'}</p>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200">Level: {formData.courseLevel || '—'}</span>
              <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200">Language: {formData.courseLanguage || '—'}</span>
              <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200">Certificate: {selectedCert ? selectedCert.template_name : (formData.certificateType || '—')}</span>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Icon icon="fluent:image-24-regular" width="18" height="18" className="text-[#4F6BFF]" />
            Thumbnail
          </h3>
          <div className="rounded-2xl border border-gray-200 overflow-hidden bg-gray-50/30">
            {formData.thumbnailPreview ? (
              <img src={formData.thumbnailPreview} alt="Course" className="w-full max-h-48 object-cover" />
            ) : (
              <div className="py-12 text-center text-gray-500 text-sm">No thumbnail</div>
            )}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Icon icon="fluent:tag-24-regular" width="18" height="18" className="text-[#4F6BFF]" />
            Tags & Info
          </h3>
          <div className="rounded-2xl border border-gray-200 bg-gray-50/30 p-5 space-y-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Tags</p>
              {formData.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 rounded-xl bg-white border border-gray-200 text-sm">{tag}</span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">None</p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Course info</p>
              {formData.courseInfo.length > 0 ? (
                <ul className="space-y-1.5">
                  {formData.courseInfo.map((info, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <Icon icon="fluent:checkmark-circle-24-regular" width="16" height="16" className="text-emerald-500 shrink-0" />
                      {info}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">None</p>
              )}
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Icon icon="fluent:payment-24-regular" width="18" height="18" className="text-[#4F6BFF]" />
            Pricing
          </h3>
          <div className="rounded-2xl border border-gray-200 bg-gray-50/30 p-5">
            {/* Price badge hidden */}
            {/* <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium ${formData.courseType === 'free' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {formData.courseType === 'free' ? 'Free' : 'Paid'}
            </span>
            {formData.courseType === 'paid' && (
              <p className="mt-3 text-gray-900 font-medium">
                {currencySymbol(formData.currency)}{formData.price || '0'}
              </p>
            )} */}
            <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium bg-emerald-100 text-emerald-800">
              Free
            </span>
          </div>
        </section>

        {formData.isMandatory && (
          <section>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Icon icon="fluent:warning-24-regular" width="18" height="18" className="text-amber-600" />
              Mandatory Course
            </h3>
            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Icon icon="fluent:shield-task-24-regular" width="24" height="24" className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-900">⚠️ Mandatory Course</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    {formData.traineeGroupIds?.length > 0
                      ? `Required for ${formData.traineeGroupIds.length} selected group(s)`
                      : 'Required for all users'}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderPricingStep = () => (
    <div className="space-y-6">
      <div>
        <label className={labelClass}>Course Type *</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label
            className={`flex gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
              formData.courseType === 'free' ? 'border-[#4F6BFF] bg-indigo-50/50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              className="sr-only"
              name="courseType"
              value="free"
              checked={formData.courseType === 'free'}
              onChange={(e) => setFormData(prev => ({ ...prev, courseType: e.target.value, price: '' }))}
            />
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <Icon icon="fluent:gift-24-regular" width="24" height="24" className="text-emerald-600" />
            </div>
            <div>
              <span className="font-medium text-gray-900 block">Free Course</span>
              <span className="text-sm text-gray-500">No payment required</span>
            </div>
          </label>
          {/* Paid Course option - hidden
          <div
            className="flex gap-4 p-4 rounded-2xl border-2 border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed relative"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Icon icon="fluent:payment-24-regular" width="24" height="24" className="text-amber-600" />
            </div>
            <div>
              <span className="font-medium text-gray-900 block">Paid Course</span>
              <span className="text-sm text-gray-500">Not available yet</span>
            </div>
            <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-amber-100 text-amber-700 text-xs font-medium">
              Coming Soon
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCourseInformationForm();
      case 2:
        return renderCourseMediaForm();
      case 3:
        return renderAdditionalInformationForm();
      case 4:
        return renderPricingStep();
      case 5:
        return renderPreviewStep();
      default:
        return null;
    }
  };

  const isEditMode = courseId && decodedCourseId;

  return (
    <AdminPage
      title={isEditMode ? 'Edit Course' : 'Create Course'}
      description={isEditMode ? 'Update course details, media, and pricing.' : 'Add a new course in 5 steps: information, media, tags, pricing, and preview.'}
      actions={
        <Link
          to="/admin/courses"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 no-underline"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Back to Courses
        </Link>
      }
    >
      {isSubmitting && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="inline-block w-12 h-12 rounded-full border-2 border-[#4F6BFF] border-t-transparent animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">{isEditMode ? 'Updating course…' : 'Creating course…'}</h3>
            <p className="text-gray-500 text-sm mt-1">Please wait.</p>
          </div>
        </div>
      )}

      {isLoadingCourse && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="inline-block w-12 h-12 rounded-full border-2 border-[#4F6BFF] border-t-transparent animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Loading course…</h3>
            <p className="text-gray-500 text-sm mt-1">Fetching details.</p>
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="mb-8">
        <div className="relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 rounded-full" style={{ marginLeft: '1.25rem', marginRight: '1.25rem' }} />
          <div
            className="absolute top-5 left-0 h-0.5 bg-[#4F6BFF] rounded-full transition-all duration-300"
            style={{
              marginLeft: '1.25rem',
              width: `calc(${((currentStep - 1) / (steps.length - 1)) * 100}% - 2.5rem)`
            }}
          />
          <div className="relative flex justify-between">
            {steps.map((step, index) => {
              const stepNum = index + 1;
              const isActive = currentStep === stepNum;
              const isDone = currentStep > stepNum;
              return (
                <div key={step.number} className="flex flex-col items-center text-center" style={{ flex: 1 }}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                      isDone ? 'bg-[#4F6BFF] text-white' : isActive ? 'bg-[#4F6BFF] text-white ring-4 ring-[#4F6BFF]/20' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isDone ? <Icon icon="fluent:checkmark-24-regular" width="20" height="20" /> : step.number}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${isActive ? 'text-[#4F6BFF]' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step content card */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-8">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-base font-semibold text-gray-900">
            {steps[currentStep - 1]?.title}
          </h2>
        </div>
        <div className="p-5 sm:p-6 lg:p-8">
          {renderStepContent()}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col-reverse sm:flex-row justify-between gap-4">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentStep === 1 || isSubmitting}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Previous
        </button>
        {currentStep === steps.length ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isCurrentStepValid() || isSubmitting}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                {isEditMode ? 'Updating…' : 'Creating…'}
              </>
            ) : (
              <>
                <Icon icon="fluent:checkmark-24-regular" width="18" height="18" />
                {isEditMode ? 'Update Course' : 'Submit Course'}
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleNext}
            disabled={!isCurrentStepValid()}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep === steps.length - 1 ? 'Preview' : 'Next'}
            <Icon icon="fluent:arrow-right-24-regular" width="18" height="18" />
          </button>
        )}
      </div>
    </AdminPage>
  );
}

export default CreateCourse;
